/** Library tokens and formatting helpers (split out of LibraryScreen). */
import type { UserDocumentItem } from '@/services/api';
import { colors } from '@/design-system';

export const isFailedTranscript = (item: UserDocumentItem) =>
  item.document_type === 'user_transcript' && item.transcription_status === 'failed';

// ─────────────────────────────────────────────────────────────────────────────
// Design Tokens - Simple, consistent colors
// ─────────────────────────────────────────────────────────────────────────────

export const COLORS = {
  background: '#F9FAFB',
  surface: '#FFFFFF',
  primary: colors.primary,
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#9CA3AF',
  },
  border: '#E5E7EB',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  voice: '#4F46E5',
  document: '#8B5CF6',
  material: '#0D9488',
};

// ─────────────────────────────────────────────────────────────────────────────
// Utility Functions
// ─────────────────────────────────────────────────────────────────────────────

export function formatDuration(seconds?: number): string {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function formatDate(
  dateString: string | undefined,
  t: (key: string, fallback?: string) => string
): string {
  if (!dateString) return '--';
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return t('library.today');
  if (diffDays === 1) return t('library.yesterday');
  if (diffDays < 7) return t('library.daysAgo').replace('{{days}}', String(diffDays));
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}


export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
